import { signal, computed } from "@preact/signals-core";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Context passed through the modal chain and available to all modals.
 */
export interface ModalContext {
  productId?: number;
  variantId?: number;
  quantity?: number;
  productTitle?: string;
  variantTitle?: string;
  customization?: Record<string, any>;
  triggerButton?: HTMLButtonElement;
  vendor?: string;
  imageUrl?: string;
  source?: string;
  /** Custom SKU variant ID — when set, a second line item is added to the cart after the main product. */
  customSkuVariantId?: number;
  /** True when the product is a voucher product — routes add-to-cart through the voucher flow. */
  isVoucher?: boolean;
  /** Selected variant SKU — required by the voucher Lambda fetch. */
  variantSku?: string;
  /** True when voucher codes should merge into one line item (MergeVoucherCodeVariants tag). */
  mergeVouchers?: boolean;
  [key: string]: any;
}

/**
 * Modal configuration for registration.
 * Modals open reactively by watching `activeModal` signal — no `show()` method needed.
 */
export interface ModalConfig {
  name: string;
  priority: number;
  shouldShow: (context: ModalContext) => boolean;
  onApprove?: (context: ModalContext) => void | Promise<void>;
  onCancel?: (context: ModalContext) => void;
}

// ─── State ────────────────────────────────────────────────────────────────────

export const registeredModals = signal<ModalConfig[]>([]);
export const activeModal = signal<string | null>(null);
export const modalContext = signal<ModalContext | null>(null);
export const isProcessing = signal(false);

// ─── Computed Values ──────────────────────────────────────────────────────────

export const sortedModals = computed(() =>
  [...registeredModals.value].sort((a, b) => a.priority - b.priority)
);

export const currentModalConfig = computed(() =>
  sortedModals.value.find((m) => m.name === activeModal.value) || null
);

// ─── Chain Promise ────────────────────────────────────────────────────────────

// Resolves the Promise returned by startModalChain when the chain ends.
let _resolveChain: ((success: boolean) => void) | null = null;

// ─── Actions ──────────────────────────────────────────────────────────────────

export const registerModal = (config: ModalConfig) => {
  if (!config.name || config.priority === undefined || !config.shouldShow) {
    console.error("[modal-store] Invalid modal config:", config);
    return;
  }
  registeredModals.value = [
    ...registeredModals.value.filter((m) => m.name !== config.name),
    config,
  ];
  console.log(`[modal-store] Registered modal: ${config.name} (priority: ${config.priority})`);
};

export const unregisterModal = (name: string) => {
  registeredModals.value = registeredModals.value.filter((m) => m.name !== name);
  console.log(`[modal-store] Unregistered modal: ${name}`);
};

/**
 * Start the modal chain with the given context.
 * Shows modals in priority order; when all are approved, executes the real
 * cart add and opens the cart. Returns true on success, false if cancelled.
 *
 * When at least one modal needs to show, this returns a Promise that stays
 * pending until the user approves or cancels (driven by approveModal /
 * cancelModal calls from the modal components themselves).
 */
export const startModalChain = (context: ModalContext): Promise<boolean> => {
  if (isProcessing.value) {
    console.warn("[modal-store] Already processing, ignoring");
    return Promise.resolve(false);
  }

  isProcessing.value = true;
  modalContext.value = context;

  const nextModal = sortedModals.value.find((m) => {
    try {
      return m.shouldShow(context);
    } catch (error) {
      console.error(`[modal-store] Error in shouldShow for ${m.name}:`, error);
      return false;
    }
  });

  if (!nextModal) {
    // No modals needed — execute add-to-cart immediately
    console.log("[modal-store] No modals to show, executing add-to-cart");
    return executeAddToCart(context).then((added) => {
      resetModalState();
      return added;
    }).catch((error) => {
      console.error("[modal-store] Error in add-to-cart:", error);
      resetModalState();
      return false;
    });
  }

  // Open first modal; the Promise resolves when approveModal/cancelModal finishes the chain
  console.log(`[modal-store] Showing modal: ${nextModal.name}`);
  activeModal.value = nextModal.name;
  return new Promise((resolve) => {
    _resolveChain = resolve;
  });
};

/**
 * Approve the current modal and advance the chain.
 * Called by modal components (via modal-base.ts → handleApprove).
 */
export const approveModal = async () => {
  const current = currentModalConfig.value;
  if (!current) {
    console.error("[modal-store] No active modal to approve");
    return;
  }

  console.log(`[modal-store] Approving modal: ${current.name}`);

  if (current.onApprove) {
    try {
      await current.onApprove(modalContext.value!);
    } catch (error) {
      console.error(`[modal-store] Error in onApprove for ${current.name}:`, error);
      _resolveChain?.(false);
      _resolveChain = null;
      resetModalState();
      return;
    }
  }

  activeModal.value = null;

  // Find the next modal in priority order
  const nextModal = sortedModals.value.find(
    (m) => m.priority > current.priority && m.shouldShow(modalContext.value!)
  );

  if (nextModal) {
    console.log(`[modal-store] Showing next modal: ${nextModal.name}`);
    activeModal.value = nextModal.name;
    // Chain continues — promise stays pending
  } else {
    console.log("[modal-store] All modals approved, executing add-to-cart");
    try {
      const added = await executeAddToCart(modalContext.value!);
      _resolveChain?.(added);
    } catch (error) {
      console.error("[modal-store] Error executing add-to-cart:", error);
      _resolveChain?.(false);
    }
    _resolveChain = null;
    resetModalState();
  }
};

/**
 * Cancel the current modal and stop the chain.
 * Called by modal components (via modal-base.ts → handleCancel).
 */
export const cancelModal = () => {
  const current = currentModalConfig.value;
  const context = modalContext.value;

  if (!current) {
    console.error("[modal-store] No active modal to cancel");
    return;
  }

  console.log(`[modal-store] Cancelling modal: ${current.name}`);

  if (current.onCancel) {
    try {
      current.onCancel(context!);
    } catch (error) {
      console.error(`[modal-store] Error in onCancel for ${current.name}:`, error);
    }
  }

  _resolveChain?.(false);
  _resolveChain = null;
  resetModalState();
};

const resetModalState = () => {
  activeModal.value = null;
  modalContext.value = null;
  isProcessing.value = false;
};

const executeAddToCart = async (context: ModalContext): Promise<boolean> => {
  if (!context.variantId) {
    console.error("[modal-store] No variant ID in context");
    return false;
  }

  console.log("[modal-store] Executing add-to-cart:", context);

  const { addToCart, openCart, publishKlaviyoAddedToCart } = await import("./cart-store");

  const analytics = { source: context.source, productId: context.productId };

  // Voucher products: fetch encoded codes and build line items via the voucher flow.
  if (context.isVoucher) {
    const { addVoucherProductToCart } = await import("./voucher-store");
    const voucherAdded = await addVoucherProductToCart(context);
    if (!voucherAdded) {
      return false;
    }
    // `kn_add_to_cart` fires per line inside executeCartAdd (1:1 with
    // product_added_to_cart). Klaviyo's whole-cart event fires once here.
    publishKlaviyoAddedToCart();
    openCart();
    return true;
  }

  // Add the main product variant
  const added = await addToCart(
    context.variantId,
    context.quantity || 1,
    context.customization,
    context.productId && context.vendor
      ? { productId: context.productId, vendor: context.vendor }
      : undefined,
    analytics
  );

  // Failsafe: if the add was blocked (e.g. by a purchase limit) or failed,
  // do not add the companion SKU and do not open the cart. The limit notice
  // is already surfaced via cartLimitError.
  if (!added) {
    return false;
  }

  // If a custom SKU variant is configured, add it as a second line item
  if (context.customSkuVariantId) {
    const skuProperties: Record<string, any> = {
      "_variant-to-customize": String(context.variantId),
      "_custom-sku-variant": "true",
    };
    if (context.customization?.["Your Text"]) {
      skuProperties["Your Text"] = context.customization["Your Text"];
    }
    await addToCart(
      context.customSkuVariantId,
      context.quantity || 1,
      skuProperties,
      undefined,
      analytics
    );
  }

  // `kn_add_to_cart` already fired per line inside executeCartAdd (main line +
  // companion SKU), staying 1:1 with product_added_to_cart. Klaviyo's
  // whole-cart event fires once here for the whole user action.
  publishKlaviyoAddedToCart();

  openCart();
  return true;
};

// ─── Debug Helpers ────────────────────────────────────────────────────────────

export const getRegisteredModals = () => {
  return registeredModals.value.map((m) => ({
    name: m.name,
    priority: m.priority,
  }));
};
